import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { SubscriptionExpiryBanner } from "@/components/SubscriptionExpiryBanner";
import { PreferencesProvider } from "@/hooks/usePreferences";
import { PreferencesPanel } from "@/components/PreferencesPanel";
import { TimerProvider } from "@/hooks/useTimers";
import { TimerDock } from "@/components/TimerDock";
import { IosInstallPrompt } from "@/components/IosInstallPrompt";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { SkipToContent } from "@/components/SkipToContent";
import { ShortcutsDialog } from "@/components/ShortcutsDialog";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { supabase } from "@/integrations/supabase/client";
import { useKeepSessionAlive } from "@/hooks/useKeepSessionAlive";
import { schedulePostLoadPrefetch } from "@/lib/prefetchRoutes";
import Index from "./pages/Index.tsx";
import ShoppingList from "./pages/ShoppingList.tsx";
const Account = lazy(() => import("./pages/Account.tsx"));
const Recipe = lazy(() => import("./pages/Recipe.tsx"));
const SharedRecipe = lazy(() => import("./pages/SharedRecipe.tsx"));
const MyRecipes = lazy(() => import("./pages/MyRecipes.tsx"));
const Menus = lazy(() => import("./pages/Menus.tsx"));
const MenuDetail = lazy(() => import("./pages/MenuDetail.tsx"));
const MenuPrint = lazy(() => import("./pages/MenuPrint.tsx"));
const Upgrade = lazy(() => import("./pages/Upgrade.tsx"));
const UpgradeExpired = lazy(() => import("./pages/UpgradeExpired.tsx"));
const Demo = lazy(() => import("./pages/Demo.tsx"));
const Bulk = lazy(() => import("./pages/Bulk.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

/**
 * Warm the most likely "next" route chunks once the browser is idle.
 * Returning users almost always head to /my-recipes or /recipe after
 * landing on `/`, so paying that download up-front (off the critical
 * path) makes those navigations feel instant. Gated on Save-Data and
 * 2G inside the helper.
 */
const RouteChunkPrefetcher = () => {
  useEffect(() => {
    schedulePostLoadPrefetch(["myRecipes", "recipe", "account"]);
  }, []);
  return null;
};

const queryClient = new QueryClient();

/**
 * Strip Supabase auth tokens from `window.location.hash` once the session
 * is established. See the original comment for the full rationale.
 */
const AuthHashCleanup = () => {
  useEffect(() => {
    const hasAuthHash = () => {
      const h = window.location.hash || "";
      return (
        h.includes("access_token=") ||
        h.includes("refresh_token=") ||
        h.includes("type=signup") ||
        h.includes("type=recovery") ||
        h.includes("type=magiclink") ||
        h.includes("type=invite")
      );
    };

    const clean = () => {
      if (!hasAuthHash()) return;
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", url);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) clean();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) clean();
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  return null;
};

const SessionKeepAlive = () => {
  useKeepSessionAlive();
  return null;
};

const ShortcutsHost = () => {
  const { shortcutsOpen, setShortcutsOpen } = useKeyboardShortcuts();
  return <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />;
};

const Wrap = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <RouteErrorBoundary routeLabel={label}>{children}</RouteErrorBoundary>
);

/**
 * Layout route — hosts every global provider, the skip link, the global
 * dock/banners, and the Suspense boundary that catches lazy route chunks.
 * Lives inside the router so child routes can use `useBlocker` (only
 * available with `createBrowserRouter`).
 */
const Layout = () => (
  <PreferencesProvider>
    <TimerProvider>
      <SkipToContent />
      <AuthHashCleanup />
      <SessionKeepAlive />
      <ShortcutsHost />
      <RouteChunkPrefetcher />
      <PaymentTestModeBanner />
      <SubscriptionExpiryBanner />
      <ErrorBoundary>
        <Suspense
          fallback={
            <div
              aria-busy="true"
              className="flex min-h-[60vh] items-center justify-center"
            >
              <div className="relative h-10 w-10">
                <div className="absolute inset-0 motion-safe:animate-ping rounded-full bg-primary/20" />
                <div className="absolute inset-2 motion-safe:animate-pulse rounded-full bg-primary/40" />
                <div className="absolute inset-[14px] rounded-full bg-primary" />
              </div>
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </ErrorBoundary>
      <PreferencesPanel />
      <TimerDock />
      <IosInstallPrompt />
      <FeedbackWidget />
    </TimerProvider>
  </PreferencesProvider>
);

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: "/", element: <Wrap label="home"><Index /></Wrap> },
      { path: "/recipe", element: <Wrap label="recipe"><Recipe /></Wrap> },
      { path: "/r/:shareId", element: <Wrap label="shared-recipe"><SharedRecipe /></Wrap> },
      { path: "/my-recipes", element: <Wrap label="my-recipes"><MyRecipes /></Wrap> },
      { path: "/menus", element: <Wrap label="menus"><Menus /></Wrap> },
      { path: "/menus/:id", element: <Wrap label="menu-detail"><MenuDetail /></Wrap> },
      { path: "/menus/:id/list", element: <Wrap label="shopping-list"><ShoppingList /></Wrap> },
      { path: "/menus/:id/print", element: <Wrap label="menu-print"><MenuPrint /></Wrap> },
      { path: "/upgrade", element: <Wrap label="upgrade"><Upgrade /></Wrap> },
      { path: "/upgrade/expired", element: <Wrap label="upgrade-expired"><UpgradeExpired /></Wrap> },
      { path: "/account", element: <Wrap label="account"><Account /></Wrap> },
      { path: "/demo", element: <Wrap label="demo"><Demo /></Wrap> },
      // /bulk route disabled — see MyRecipesLink.
      { path: "*", element: <Wrap label="not-found"><NotFound /></Wrap> },
    ],
  },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <RouterProvider router={router} />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
